// Resolve which patient-facing messaging channels this site may actually use.
//
// Teamio sells Tx Presenter to two segments: All-in-One offices (Teamio texting
// provisioned) and Core-plan offices, which text patients through a competitor's
// product and use the "Copy link" path instead. Nothing in teamio.settings names
// the plan, so capability is derived from the signals the rest of the app already
// treats as the source of truth:
//
//   SMS   → GENERAL_PHONE            (the provisioned Twilio number — the identical
//                                     check gates Conversations in src/config/ability.js,
//                                     campaigns in CreateCampaignModal.vue, and
//                                     follow-ups in CreateFollowup.vue)
//   EMAIL → GENERAL_VERIFIED_EMAILS  (the per-site verified SendGrid sender)
//
// DEFAULT-DENY, and deliberately no new settings key: a Core office needs no
// provisioning to be correct, and an office that later buys All-in-One lights up
// the moment A2P writes GENERAL_PHONE.
//
// IMPORTANT: keep this in sync with the backend twin at
// teamio-serverless-api/tx-presenter-service/lib/resolveChannels.js — the server
// independently enforces the same rules, and a UI that disagrees with it either
// hides something that works or offers something that will 403.
//
// This file deliberately takes raw PropertyValue strings rather than reaching into
// the store, so it stays a pure function that the twin fixture tests can drive.

// The verified-sender parse lives in ONE place on the frontend — see that file for
// the five storage shapes and why a plain JSON.parse (or a raw passthrough) is
// wrong. Re-exported here so Tx Presenter call sites keep importing it from the
// resolver they already use, without a second copy existing.
//
// Its backend counterpart is tx-presenter-service/lib/resolveChannels.js, which
// carries the same parse — those two must stay in sync.
import { pickVerifiedFromEmail } from '@/utils/verifiedSender.js';
// The ONE frontend parser of SMS_OUTBOUND_DISABLED (mirrors the layer's killSwitch). Never
// re-parse the raw row here — cypress/node-tests/sms-outbound-policy-agreement.test.js
// executes this file next to the layer and the backend twin and fails if they disagree.
import { isSmsOutboundDisabled } from '@/utils/siteCapability.js';

/**
 * @param {object}  args
 * @param {string=} args.generalPhone          - GENERAL_PHONE PropertyValue
 * @param {string=} args.generalVerifiedEmails - GENERAL_VERIFIED_EMAILS PropertyValue (raw JSON)
 * @param {boolean=} args.smsOutboundDisabled  - the PARSED SMS_OUTBOUND_DISABLED boolean (strict true only)
 * @returns {{
 *   sms:   { available: boolean, reason: 'ok'|'no-teamio-texting'|'sms-disabled' },
 *   email: { available: boolean, reason: 'ok'|'no-verified-email' },
 *   fromEmail: string|null,
 *   anyAvailable: boolean
 * }}
 *
 * Note: the backend twin can also return sms.reason === 'twilio-misconfigured'
 * (a provisioned number whose subaccount creds are missing). The frontend can't
 * see teamio.sites, so it never produces that reason — it resolves such a site as
 * available and lets the server surface the real failure.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Platform email sender (T5/T11) — MIRROR of the backend twin. Keep in sync.
//
// T5: the sending domain is defined here and in the backend resolver, nowhere
// else. Never write '@mail.teamio.app' into a component.
// ─────────────────────────────────────────────────────────────────────────────
const PLATFORM_EMAIL_DOMAIN = 'mail.teamio.app';

const RESERVED_LOCAL_PARTS = [
  'noreply', 'no-reply', 'postmaster', 'abuse', 'admin', 'support',
  'info', 'help', 'billing', 'bounce', 'mailer-daemon', 'teamio',
];

const MAX_LOCAL_PART_LEN = 30;

/** T11 — CANDIDATE local part from a practice name. Never call at send time. */
function derivePlatformLocalPart(practiceName) {
  const slug = String(practiceName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, MAX_LOCAL_PART_LEN);
  if (!slug) return null;
  if (RESERVED_LOCAL_PARTS.indexOf(slug) !== -1) return null;
  return slug;
}

/** Compose the full platform address from a stored local part. */
function buildPlatformAddress(localPart) {
  const lp = String(localPart || '').trim();
  return lp ? `${lp}@${PLATFORM_EMAIL_DOMAIN}` : null;
}

/**
 * The office address a PATIENT sees and replies to when we send on the practice's
 * behalf. MIRROR of the backend helper — keep byte-for-byte in step.
 *
 *   1. `officeReplyEmail` — the dedicated, patient-facing field
 *   2. else `notificationEmails[0]` — LEGACY FALLBACK, permanent
 *   3. else ''  → the resolver refuses with 'platform-sender-not-configured'
 *
 * Why the fallback: until 2026-08-30 the platform Reply-To WAS
 * `notificationEmails[0]` — a list labelled and understood as INTERNAL staff
 * alerting, where only entry [0] mattered and nothing said so. The fallback keeps
 * any site enabled under the old rule resolving identically; no migration.
 */
function resolveOfficeReplyEmail(general) {
  const g = general && typeof general === 'object' ? general : {};
  const explicit = String(g.officeReplyEmail || '').trim();
  if (explicit.indexOf('@') !== -1) return explicit;
  const list = Array.isArray(g.notificationEmails) ? g.notificationEmails : [];
  for (const entry of list) {
    const e = String(entry || '').trim();
    if (e.indexOf('@') !== -1) return e;
  }
  return '';
}

function resolveSiteChannels({
  generalPhone, generalVerifiedEmails,
  // Platform-sender inputs — all OPTIONAL. Omit them and this behaves exactly as
  // it did before the platform sender existed (see the backend twin's note).
  platformSenderEnabled, platformSenderLocalPart, practiceName, replyToEmail,
  // WhatsApp-only office — MIRROR of the backend twin. Optional: omitted ⇒ exactly as before.
  // Strict `=== true`: only the parsed boolean switches texting off, never a stray string.
  smsOutboundDisabled,
} = {}) {
  const phone = typeof generalPhone === 'string' ? generalPhone.trim() : '';
  const fromEmail = pickVerifiedFromEmail(generalVerifiedEmails);

  // No number outranks the policy (a Core office is not "WhatsApp-only"), same as the backend.
  let sms;
  if (!phone) sms = { available: false, reason: 'no-teamio-texting' };
  else if (smsOutboundDisabled === true) sms = { available: false, reason: 'sms-disabled' };
  else sms = { available: true, reason: 'ok' };

  // THE FLIP RULE — a site's own verified sender always wins; the platform sender
  // is only ever a fallback, never a parallel channel.
  let email;
  let sender = null;
  let resolvedFromEmail = fromEmail;

  if (fromEmail) {
    email = { available: true, reason: 'ok' };
    sender = {
      email: fromEmail,
      name: practiceName || '',
      // ⛔ DELIBERATELY null for own-sender sites — mirrors the backend twin.
      // Reply-To only matters when the From is NOT the practice's own address;
      // setting it here would silently redirect patient replies at working sites.
      replyTo: null,
      isPlatform: false,
    };
  } else if (platformSenderEnabled === true) {
    // Strict === true: absent ⇒ OFF (D5 is fail-closed).
    const platformAddress = buildPlatformAddress(platformSenderLocalPart);
    const replyTo = typeof replyToEmail === 'string' ? replyToEmail.trim() : '';
    if (platformAddress && replyTo) {
      email = { available: true, reason: 'platform-sender' };
      sender = { email: platformAddress, name: practiceName || '', replyTo, isPlatform: true };
      resolvedFromEmail = platformAddress;
    } else {
      email = { available: false, reason: 'platform-sender-not-configured' };
    }
  } else {
    email = { available: false, reason: 'no-verified-email' };
  }

  return {
    sms,
    email,
    fromEmail: resolvedFromEmail,
    sender,
    anyAvailable: sms.available || email.available,
  };
}

/**
 * Convenience adapter: resolve straight from the raw `customer.settings` array
 * (the shape the store holds). Kept separate from resolveSiteChannels so the pure
 * resolver stays trivially testable.
 *
 * Reads the ARRAY, not `customer.allSettings` — that property does not exist, and
 * reading it is what made the PAYMENT_ENABLED gate in PresentDepositPanel.vue
 * return false on every site. See that file's note at the `paymentProvider` prop.
 *
 * @param {Array<{Property: string, PropertyValue: string}>|null|undefined} settings
 */
function resolveSiteChannelsFromSettings(settings) {
  const find = (prop) => {
    if (!Array.isArray(settings)) return undefined;
    const row = settings.find((s) => s && s.Property === prop);
    return row ? row.PropertyValue : undefined;
  };
  // Reply-To and the display name come out of the TX_PRESENTER_SETTINGS_GENERAL
  // JSON blob, not a flat setting. notificationEmails is the list the office
  // already fills in for "who gets told a patient commented" (T6) — reused as the
  // patient-facing Reply-To, FIRST entry only (SendGrid reply_to is single-valued).
  let txGeneral = {};
  try { txGeneral = JSON.parse(find('TX_PRESENTER_SETTINGS_GENERAL') || '{}') || {}; } catch (e) { txGeneral = {}; }
  const replyToEmail = resolveOfficeReplyEmail(txGeneral);

  return resolveSiteChannels({
    generalPhone: find('GENERAL_PHONE'),
    generalVerifiedEmails: find('GENERAL_VERIFIED_EMAILS'),
    // Booleans are stored as the strings 'TRUE'/'FALSE'; absent ⇒ OFF (D5).
    platformSenderEnabled: String(find('GENERAL_PLATFORM_SENDER_ENABLED') || '').toUpperCase() === 'TRUE',
    platformSenderLocalPart: find('GENERAL_PLATFORM_SENDER_LOCALPART'),
    practiceName: (txGeneral.practiceName || find('GENERAL_PRACTICE_NAME') || '').trim(),
    replyToEmail,
    smsOutboundDisabled: isSmsOutboundDisabled(Array.isArray(settings) ? settings : null),
  });
}

export {
  pickVerifiedFromEmail,
  resolveSiteChannels,
  resolveSiteChannelsFromSettings,
  derivePlatformLocalPart,
  resolveOfficeReplyEmail,
  buildPlatformAddress,
  PLATFORM_EMAIL_DOMAIN,
  RESERVED_LOCAL_PARTS,
};
